import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Twitter, Linkedin, Instagram, Youtube, Github, Globe, Mail, Phone, MapPin, ArrowUp } from 'lucide-react';
import { useBranding } from '@/contexts/BrandContext';
import { useFooterPreset } from '@/hooks/usePresets';
import { usePageTypeContext } from '@/contexts/PageTypeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Logo height presets in pixels
const LOGO_HEIGHT_PX = {
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
};

const SOCIAL_ICON = {
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
  github: Github,
};

const CONTAINER_WIDTH = {
  full: 'max-w-none',
  wide: 'max-w-screen-2xl',
  default: 'max-w-7xl',
};

const NDIS_TOOL_LINKS = [
  { id: 'fl-ndis-pricelist', label: 'NDIS price list', url: '/ndis-pricelist', is_external: false, is_active: true },
  { id: 'fl-ndis-budget', label: 'Budget calculator', url: '/ndis-budget-calculator', is_external: false, is_active: true },
];

export function withNdisToolLinks(preset) {
  if (!preset || preset.hidden) return preset;
  const columns = preset.columns || [];
  const hasLink = columns.some((col) => (col.links || []).some((link) => (
    link.url === '/ndis-pricelist' || link.url === '/ndis-budget-calculator'
  )));
  if (preset.show_columns && columns.length > 0) {
    if (hasLink) return preset;
    const target = columns.find((col) => /resource/i.test(col.title || ''))
      || columns.find((col) => /product/i.test(col.title || ''))
      || columns[columns.length - 1];
    return {
      ...preset,
      columns: columns.map((col) => {
        if (col !== target && col.id !== target.id) return col;
        const existing = col.links || [];
        return {
          ...col,
          links: [
            ...existing,
            ...NDIS_TOOL_LINKS.map((link, index) => ({
              ...link,
              order: existing.length + index,
            })),
          ],
        };
      }),
    };
  }
  return { ...preset, ndisToolStrip: NDIS_TOOL_LINKS };
}

function FooterLink({ link }) {
  const isExternal = link.is_external || /^https?:\/\//.test(link.url || '');
  const cls = 'text-sm text-slate-500 hover:text-indigo-600 transition-colors';
  const testId = link.url === '/ndis-pricelist'
    ? 'footer-ndis-pricelist'
    : link.url === '/ndis-budget-calculator'
      ? 'footer-ndis-budget'
      : undefined;
  if (isExternal) {
    return <a href={link.url} target="_blank" rel="noopener noreferrer" className={cls}>{link.label}</a>;
  }
  return <Link to={link.url || '/'} className={cls} data-testid={testId}>{link.label}</Link>;
}

export default function PublicFooter({ pageType: pageTypeProp = null, presetOverrideId = null }) {
  const { branding } = useBranding();
  const { pageType: ctxPageType, footerPresetOverride: ctxFooterOverride } = usePageTypeContext();
  const pageType = pageTypeProp || ctxPageType || 'marketing';
  const overrideId = presetOverrideId || ctxFooterOverride || null;
  const { preset, loaded } = useFooterPreset(pageType, overrideId);
  const [legalPages, setLegalPages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/site-settings/legal-pages-footer`)
      .then((response) => {
        if (cancelled) return;
        const pages = (response.data.pages || []).sort((a, b) => (a.footer_order || 0) - (b.footer_order || 0));
        setLegalPages(pages);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;
  if (!preset || preset.hidden) return null;

  const siteName = branding?.site_name || 'Provisoft';
  const year = new Date().getFullYear();
  const copyrightText = (preset.copyright_text || '© {year} {site_name}. All rights reserved.')
    .replace('{year}', String(year))
    .replace('{site_name}', siteName);

  const isDark = preset.background === 'dark' || preset.text_color === 'light';
  const bgClass = {
    white: 'bg-white border-t border-indigo-50 text-slate-700',
    slate: 'bg-slate-50 border-t border-slate-200 text-slate-700',
    dark: 'bg-slate-900 text-slate-300',
    gradient: 'bg-gradient-to-br from-indigo-50 via-white to-indigo-50 border-t border-indigo-50 text-slate-700',
  }[preset.background || 'white'];

  const headingColor = isDark ? 'text-white' : 'text-slate-900';
  const mutedColor = isDark ? 'text-slate-400' : 'text-slate-500';
  const containerClass = CONTAINER_WIDTH[preset.container_width || 'default'] || CONTAINER_WIDTH.default;

  // Logo configuration from preset
  const logoVariant = preset.logo_variant || 'auto';
  const logoHeightSetting = preset.logo_height || 'md';
  const logoHeightPx = logoHeightSetting === 'custom' 
    ? (preset.logo_height_px || 32) 
    : (LOGO_HEIGHT_PX[logoHeightSetting] || 32);
  const logoMaxWidth = preset.logo_max_width || 160;

  // Get preset-specific logos (if any)
  const presetLogos = preset?.logos || {};

  // Determine which logo to use based on variant and background
  // Priority: preset-specific logos > branding logos
  const getLogoUrl = () => {
    if (logoVariant === 'icon') return presetLogos.logo_icon || branding?.logo_icon;
    if (logoVariant === 'light') return presetLogos.logo_light || branding?.logo_light;
    if (logoVariant === 'dark') return presetLogos.logo_dark || branding?.logo_dark;
    // Auto: pick logo based on background darkness
    if (isDark) return presetLogos.logo_dark || branding?.logo_dark || presetLogos.logo_light || branding?.logo_light;
    return presetLogos.logo_light || branding?.logo_light || presetLogos.logo_dark || branding?.logo_dark;
  };

  const logoUrl = getLogoUrl();

  const view = withNdisToolLinks(preset);
  const hasBody = view.show_brand_block || view.show_columns || view.show_social;

  return (
    <footer className={`${bgClass}`} data-testid="public-footer">
      <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8 py-12`}>
        {hasBody && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-10">
            {preset.show_brand_block && (
              <div className="lg:col-span-4 space-y-4" data-testid="footer-brand-block">
                <div className="flex items-center gap-2">
                  {logoUrl ? (
                    <img 
                      src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`} 
                      alt={siteName} 
                      style={{ height: `${logoHeightPx}px`, maxWidth: `${logoMaxWidth}px` }}
                      className="w-auto object-contain"
                    />
                  ) : (
                    <span className={`text-xl font-bold ${headingColor}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{siteName}</span>
                  )}
                </div>
                {preset.brand_blurb && (
                  <p className={`text-sm ${mutedColor} max-w-xs`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {preset.brand_blurb}
                  </p>
                )}
                {(preset.contact_email || preset.contact_phone || preset.contact_address) && (
                  <ul className="space-y-2 text-sm">
                    {preset.contact_email && (
                      <li className="flex items-center gap-2">
                        <Mail className={`h-4 w-4 ${mutedColor}`} />
                        <a href={`mailto:${preset.contact_email}`} className={`${mutedColor} hover:text-indigo-600`}>{preset.contact_email}</a>
                      </li>
                    )}
                    {preset.contact_phone && (
                      <li className="flex items-center gap-2">
                        <Phone className={`h-4 w-4 ${mutedColor}`} />
                        <a href={`tel:${preset.contact_phone}`} className={`${mutedColor} hover:text-indigo-600`}>{preset.contact_phone}</a>
                      </li>
                    )}
                    {preset.contact_address && (
                      <li className="flex items-start gap-2">
                        <MapPin className={`h-4 w-4 mt-0.5 ${mutedColor}`} />
                        <span className={mutedColor}>{preset.contact_address}</span>
                      </li>
                    )}
                  </ul>
                )}
                {preset.show_social && (preset.social_links || []).filter((s) => s.is_active && s.url).length > 0 && (
                  <div className="flex items-center gap-3 pt-2" data-testid="footer-social-row">
                    {(preset.social_links || []).filter((s) => s.is_active && s.url).map((s) => {
                      const Icon = SOCIAL_ICON[s.platform] || Globe;
                      return (
                        <a
                          key={s.platform + s.url}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={s.platform}
                          className={`w-9 h-9 rounded-xl flex items-center justify-center transition ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-indigo-50 text-slate-600 hover:text-indigo-600'}`}
                          data-testid={`footer-social-${s.platform}`}
                        >
                          <Icon className="h-4 w-4" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {view.show_columns && (view.columns || []).length > 0 && (
              <div className={`${view.show_brand_block ? 'lg:col-span-8' : 'lg:col-span-12'} grid grid-cols-2 md:grid-cols-${Math.min((view.columns || []).length, 4)} gap-6`} data-testid="footer-columns">
                {(view.columns || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((col) => (
                  <div key={col.id} className="space-y-3" data-testid={`footer-col-${col.id}`}>
                    {col.title && (
                      <h3 className={`text-xs font-semibold uppercase tracking-wider ${headingColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {col.title}
                      </h3>
                    )}
                    <ul className="space-y-2">
                      {(col.links || []).filter((l) => l.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((link) => (
                        <li key={link.id || link.label + link.url}>
                          <FooterLink link={link} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(preset.show_copyright || preset.show_legal_strip || preset.show_back_to_top) && (
          <div className={`pt-6 ${hasBody ? `border-t ${isDark ? 'border-white/10' : 'border-slate-200'}` : ''} flex flex-col sm:flex-row items-center justify-between gap-4`} data-testid="footer-bottom-strip">
            {preset.show_copyright && (
              <p className={`text-xs ${mutedColor}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="footer-copyright">
                {copyrightText}
              </p>
            )}
            {(view.ndisToolStrip || []).length > 0 && (
              <nav className="flex items-center gap-4 flex-wrap justify-center" aria-label="NDIS tools" data-testid="footer-ndis-tools">
                {view.ndisToolStrip.map((link) => (
                  <Link
                    key={link.id}
                    to={link.url}
                    className={`text-xs ${mutedColor} hover:text-indigo-600 transition-colors`}
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid={`footer-${link.id}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            )}
            {preset.show_legal_strip && legalPages.length > 0 && (
              <nav className="flex items-center gap-4 flex-wrap justify-center" aria-label="Legal" data-testid="footer-legal-strip">
                {legalPages.map((lp) => (
                  <Link
                    key={lp.slug}
                    to={lp.slug}
                    className={`text-xs ${mutedColor} hover:text-indigo-600 transition-colors`}
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid={`footer-legal-${lp.slug.replace(/\//g, '-')}`}
                  >
                    {lp.display_name}
                  </Link>
                ))}
              </nav>
            )}
            {preset.show_back_to_top && (
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`inline-flex items-center gap-1 text-xs ${mutedColor} hover:text-indigo-600 transition`}
                data-testid="footer-back-to-top"
              >
                <ArrowUp className="h-3.5 w-3.5" /> Back to top
              </button>
            )}
          </div>
        )}
      </div>
    </footer>
  );
}
